import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Bed, 
  Bath, 
  Users, 
  MapPin, 
  Wifi, 
  Utensils, 
  Tv, 
  Car, 
  Check, 
  ArrowLeft,
  Wind,
  Flame,
  Coffee,
  Refrigerator,
  Waves,
  UtensilsCrossed,
  Wine,
  TableProperties,
  WashingMachine,
  ShowerHead,
  Shirt,
  Sparkles,
  Moon,
  Bed as BedIcon,
  ShieldAlert,
  AlertTriangle,
  Droplets,
  FlaskConical,
  Plus,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PropertyDetail = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/properties/${id}`);
        if (!response.ok) throw new Error('Property not found');
        const data = await response.json();
        // Convert snake_case to camelCase for frontend compatibility
        const formattedData = {
          ...data,
          pricePerNight: data.price_per_night,
          bookingUrl: data.booking_url,
          bedConfiguration: data.bed_configuration
        };
        setProperty(formattedData);
      } catch (err) {
        console.error('Error fetching property:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-slate-600" />
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Property Not Found</h2>
          <Link to="/properties">
            <Button className="bg-slate-800 hover:bg-slate-700 text-white">
              View All Properties
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const amenityIcons = {
    'Air Conditioning': <Wind className="h-5 w-5" />,
    'Heating': <Flame className="h-5 w-5" />,
    'Hot Water': <Droplets className="h-5 w-5" />,
    'WiFi': <Wifi className="h-5 w-5" />,
    'TV': <Tv className="h-5 w-5" />,
    'Sound System': <Tv className="h-5 w-5" />,
    'Dedicated Workspace': <TableProperties className="h-5 w-5" />,
    'Kitchen': <Utensils className="h-5 w-5" />,
    'Kitchenette': <Utensils className="h-5 w-5" />,
    'Refrigerator': <Refrigerator className="h-5 w-5" />,
    'Freezer': <Refrigerator className="h-5 w-5" />,
    'Microwave': <UtensilsCrossed className="h-5 w-5" />,
    'Dishwasher': <Sparkles className="h-5 w-5" />,
    'Oven': <Utensils className="h-5 w-5" />,
    'Stove': <Flame className="h-5 w-5" />,
    'Coffee Maker': <Coffee className="h-5 w-5" />,
    'Hot Water Kettle': <Coffee className="h-5 w-5" />,
    'Toaster': <UtensilsCrossed className="h-5 w-5" />,
    'Blender': <UtensilsCrossed className="h-5 w-5" />,
    'Cooking Basics': <Utensils className="h-5 w-5" />,
    'Baking Sheet': <Utensils className="h-5 w-5" />,
    'Dishes & Silverware': <UtensilsCrossed className="h-5 w-5" />,
    'Wine Glasses': <Wine className="h-5 w-5" />,
    'Dining Table': <TableProperties className="h-5 w-5" />,
    'Washer': <WashingMachine className="h-5 w-5" />,
    'Dryer': <Wind className="h-5 w-5" />,
    'Iron': <Shirt className="h-5 w-5" />,
    'Hangers': <Shirt className="h-5 w-5" />,
    'Bed Linens': <BedIcon className="h-5 w-5" />,
    'Clothing Storage': <Shirt className="h-5 w-5" />,
    'Extra Pillows & Blankets': <BedIcon className="h-5 w-5" />,
    'Room-Darkening Shades': <Moon className="h-5 w-5" />,
    'Essentials': <Check className="h-5 w-5" />,
    'Bathtub': <Waves className="h-5 w-5" />,
    'Hair Dryer': <Wind className="h-5 w-5" />,
    'Shampoo': <Droplets className="h-5 w-5" />,
    'Conditioner': <Droplets className="h-5 w-5" />,
    'Shower Gel': <ShowerHead className="h-5 w-5" />,
    'Body Soap': <Droplets className="h-5 w-5" />,
    'Cleaning Products': <Sparkles className="h-5 w-5" />,
    'First Aid Kit': <Plus className="h-5 w-5" />,
    'Fire Extinguisher': <AlertTriangle className="h-5 w-5" />,
    'Smoke Alarm': <AlertTriangle className="h-5 w-5" />,
    'Carbon Monoxide Alarm': <ShieldAlert className="h-5 w-5" />,
    'Trash Compactor': <Check className="h-5 w-5" />,
    'Free Parking on Premises': <Car className="h-5 w-5" />,
    'Free Street Parking': <Car className="h-5 w-5" />,
    'Paid Parking on Premises': <Car className="h-5 w-5" />,
    'Backyard': <MapPin className="h-5 w-5" />,
    'Outdoor Dining Area': <MapPin className="h-5 w-5" />,
    'Long Term Stays Allowed': <Check className="h-5 w-5" />,
    'Smart TV': <Tv className="h-5 w-5" />,
    'Free Parking': <Car className="h-5 w-5" />,
    'Parking': <Car className="h-5 w-5" />,
    'Full Kitchen': <Utensils className="h-5 w-5" />,
    'Gourmet Kitchen': <Utensils className="h-5 w-5" />,
    'Washer/Dryer': <WashingMachine className="h-5 w-5" />,
    'Work Space': <TableProperties className="h-5 w-5" />,
    'Work Desk': <TableProperties className="h-5 w-5" />,
    'Central AC': <Wind className="h-5 w-5" />,
    'Linens Provided': <BedIcon className="h-5 w-5" />,
    'Patio': <MapPin className="h-5 w-5" />,
    'Private Rooftop': <MapPin className="h-5 w-5" />,
    'City Views': <MapPin className="h-5 w-5" />,
    'Balcony': <MapPin className="h-5 w-5" />,
    'Fireplace': <Flame className="h-5 w-5" />,
    'Gym Access': <Users className="h-5 w-5" />,
    'Gym': <Users className="h-5 w-5" />
  };

  return (
    <div className="bg-slate-50 min-h-screen py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link to="/properties" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6 transition-colors duration-200">
          <ArrowLeft className="h-4 w-4 mr-2" />
          <span>Back to Properties</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-2">
            {/* Main Image */}
            <div className="relative h-96 rounded-lg overflow-hidden mb-4">
              <img
                src={property.images[selectedImage]}
                alt={property.name}
                className="w-full h-full object-cover"
              />
              {property.featured && (
                <Badge className="absolute top-4 left-4 bg-slate-800 text-white">
                  Featured Property
                </Badge>
              )}
            </div>

            {/* Thumbnail Images */}
            {property.images.length > 1 && (
              <div className="grid grid-cols-4 gap-2 mb-8">
                {property.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`relative h-24 rounded-lg overflow-hidden ${
                      selectedImage === idx ? 'ring-2 ring-slate-800' : 'opacity-70 hover:opacity-100'
                    } transition-all duration-200`}
                  >
                    <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Property Details */}
            <Card className="mb-8">
              <CardContent className="p-6">
                <h1 className="text-3xl font-bold text-slate-900 mb-2">{property.name}</h1>
                <div className="flex items-center text-slate-600 mb-4">
                  <MapPin className="h-5 w-5 mr-2" />
                  <span className="text-lg">{property.location}</span>
                </div>
                <div className="flex items-center gap-6 mb-6 pb-6 border-b border-slate-200">
                  <div className="flex items-center text-slate-700">
                    <Bed className="h-5 w-5 mr-2" />
                    <span className="font-medium">{property.bedrooms} Bedroom{property.bedrooms > 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex items-center text-slate-700">
                    <Bath className="h-5 w-5 mr-2" />
                    <span className="font-medium">{property.bathrooms} Bath{property.bathrooms > 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex items-center text-slate-700">
                    <Users className="h-5 w-5 mr-2" />
                    <span className="font-medium">Sleeps {property.sleeps}</span>
                  </div>
                </div>

                {/* Bed Configuration */}
                {property.bedConfiguration && (
                  <div className="mb-6 pb-6 border-b border-slate-200">
                    <h3 className="text-lg font-bold text-slate-900 mb-3">Sleeping Arrangements</h3>
                    <div className="flex flex-wrap gap-4">
                      {property.bedConfiguration.map((bed, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-slate-50 px-4 py-3 rounded-lg">
                          <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                            <BedIcon className="h-5 w-5 text-amber-700" />
                          </div>
                          <div>
                            <span className="font-semibold text-slate-900">{bed.count}x</span>
                            <span className="text-slate-700 ml-1">{bed.type}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <h2 className="text-xl font-bold text-slate-900 mb-3">About This Property</h2>
                <p className="text-slate-600 leading-relaxed mb-6">{property.description}</p>

                {/* Property Highlights */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900">{property.bedrooms}</div>
                    <div className="text-sm text-slate-600">Bedrooms</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900">{property.bathrooms}</div>
                    <div className="text-sm text-slate-600">Bathrooms</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900">{property.sleeps}</div>
                    <div className="text-sm text-slate-600">Max Guests</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-amber-600">${property.pricePerNight}</div>
                    <div className="text-sm text-slate-600">Per Night</div>
                  </div>
                </div>

                {/* Local Guide Link */}
                <Link to={`/local-guide/${id}`} className="block">
                  <div className="bg-slate-800 hover:bg-slate-700 text-white rounded-lg p-4 flex items-center justify-between transition-colors">
                    <div>
                      <p className="font-semibold">Explore the Area</p>
                      <p className="text-sm text-slate-300">Local Guide for This Property</p>
                    </div>
                    <ArrowLeft className="h-5 w-5 rotate-180" />
                  </div>
                </Link>
              </CardContent>
            </Card>

            {/* Amenities */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Amenities</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {property.amenities.map((amenity, idx) => (
                    <div key={idx} className="flex items-center space-x-2 text-slate-700">
                      <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                        {amenityIcons[amenity] || <Check className="h-5 w-5" />}
                      </div>
                      <span>{amenity}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Booking Card */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 border-slate-200">
              <CardContent className="p-6">
                <div className="text-center mb-6 pb-6 border-b border-slate-200">
                  <div className="text-3xl font-bold text-slate-900 mb-1">
                    ${property.pricePerNight}
                    <span className="text-lg font-normal text-slate-600">/night</span>
                  </div>
                  <p className="text-sm text-slate-500">Save 15% booking direct</p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Base Rate</span>
                    <span className="font-medium text-slate-900">${property.pricePerNight}/night</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Cleaning Fee</span>
                    <span className="font-medium text-slate-900">$75</span>
                  </div>
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Direct Booking Savings</span>
                    <span className="font-medium">-15%</span>
                  </div>
                </div>

                <a href={property.bookingUrl || '/properties'} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full mb-3 bg-amber-500 hover:bg-amber-600 text-slate-900 py-6 text-lg font-bold">
                    Book This Property
                  </Button>
                </a>
                <p className="text-xs text-center text-slate-500">
                  Reserve now and pay later. Full refund for cancellations 30+ days before arrival.
                </p>

                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-3">Why Book Direct?</h3>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start">
                      <Check className="h-4 w-4 mr-2 mt-0.5 text-slate-700" />
                      <span>Best price guarantee</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-4 w-4 mr-2 mt-0.5 text-slate-700" />
                      <span>No hidden fees</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-4 w-4 mr-2 mt-0.5 text-slate-700" />
                      <span>Direct communication with host</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-4 w-4 mr-2 mt-0.5 text-slate-700" />
                      <span>Flexible policies</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;